import { useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { AppGrid } from '../components/AppGrid';
import { useAppCatalog } from '../hooks/useAppCatalog';
import { COLORS, FONTS } from '../styles/tokens';

/** Browse and request access to additional applications */
export function AppStore(): React.JSX.Element {
  const { roles } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const { apps } = useAppCatalog();
  const isAdmin = roles.includes('admin');

  /** Apps the user can access */
  const myApps = useMemo(
    () => isAdmin ? apps : apps.filter(app => roles.includes(app.requiredRole)),
    [apps, roles, isAdmin],
  );

  /** Apps the user does NOT have access to */
  const unavailableApps = useMemo(
    () => isAdmin ? [] : apps.filter(app => !roles.includes(app.requiredRole)),
    [apps, roles, isAdmin],
  );

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2
          className="vx-track"
          style={{
            fontFamily: FONTS.heading,
            fontSize: '22px',
            letterSpacing: '0.2em',
            color: colors.text,
            textTransform: 'uppercase',
            margin: '0 0 8px',
          }}
        >
          App Store
        </h2>
        <p
          style={{
            fontFamily: FONTS.body,
            fontSize: '14px',
            color: colors.textSecondary,
            margin: 0,
          }}
        >
          Browse available applications in the Vertex ecosystem
        </p>
      </div>

      {/* Available apps */}
      {myApps.length > 0 && (
        <section style={{ marginBottom: '40px' }}>
          <h3
            style={{
              fontFamily: FONTS.heading,
              fontSize: '14px',
              letterSpacing: '0.15em',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              margin: '0 0 16px',
            }}
          >
            Your Apps
          </h3>
          <AppGrid apps={myApps} />
        </section>
      )}

      {/* Apps needing access request */}
      {unavailableApps.length > 0 && (
        <section>
          <h3
            style={{
              fontFamily: FONTS.heading,
              fontSize: '14px',
              letterSpacing: '0.15em',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              margin: '0 0 8px',
            }}
          >
            Request Access
          </h3>
          <p
            style={{
              fontFamily: FONTS.body,
              fontSize: '13px',
              color: colors.textSecondary,
              margin: '0 0 16px',
            }}
          >
            Contact your administrator to request access to these applications.
          </p>
          <div style={{ opacity: 0.6 }}>
            <AppGrid apps={unavailableApps} locked />
          </div>
        </section>
      )}
    </div>
  );
}
